import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Todo } from '@ext-bookmark/shared/types';
import {
  getTodosFromSupabase,
  getPersonalTodosFromSupabase,
  getGroupTodosFromSupabase,
  saveTodoToSupabase,
  deleteTodoFromSupabase,
  deleteTodosFromSupabase,
} from '../../services/todo/supabaseStorage';

const TODOS_STALE_TIME_MS = 0; // 不缓存，实时拉取
const TODOS_GC_TIME_MS = 30 * 1000; // 30 seconds

const commonTodoQueryOptions = {
  staleTime: TODOS_STALE_TIME_MS,
  gcTime: TODOS_GC_TIME_MS,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
};

export type UseTodosQueryOptions = { enabled?: boolean };

/**
 * Fetch all todos for the current user (RLS-filtered) via React Query.
 * Query key: ['todos'].
 */
export function useTodosQuery(options?: UseTodosQueryOptions) {
  return useQuery({
    queryKey: ['todos'],
    queryFn: async (): Promise<Todo[]> => {
      const { todos, error } = await getTodosFromSupabase(undefined, { noCache: true });
      if (error) throw new Error(error.message);
      return todos;
    },
    ...commonTodoQueryOptions,
    ...(options && { enabled: options.enabled }),
  });
}

/**
 * Fetch personal-group todos. Query key: ['todos', 'personal'].
 */
export function usePersonalTodosQuery() {
  return useQuery({
    queryKey: ['todos', 'personal'],
    queryFn: async (): Promise<Todo[]> => {
      const { todos, error } = await getPersonalTodosFromSupabase();
      if (error) {
        throw new Error(error.message);
      }
      return todos;
    },
    ...commonTodoQueryOptions,
  });
}

/**
 * Fetch todos for a given group. Query key: ['todos', 'group', groupId].
 * Enabled only when groupId is present.
 */
export function useGroupTodosQuery(groupId: string | null | undefined) {
  return useQuery({
    queryKey: ['todos', 'group', groupId],
    queryFn: async (): Promise<Todo[]> => {
      if (!groupId) return [];
      const { todos, error } = await getGroupTodosFromSupabase(groupId, undefined, { noCache: true });
      if (error) throw new Error(error.message);
      return todos;
    },
    enabled: !!groupId,
    ...commonTodoQueryOptions,
  });
}

// --- Mutations ---

/**
 * Save (create/update) a todo to Supabase. On success merges the saved todo into ['todos'] cache (mainline-data-flow: 局部更新，不整表 invalidate).
 */
export function useSaveTodoMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (todo: Todo) => {
      const { todo: saved, error } = await saveTodoToSupabase(todo);
      if (error) throw new Error(error.message);
      return saved ?? todo;
    },
    onSuccess: (savedTodo) => {
      queryClient.setQueryData<Todo[]>(['todos'], (old) => {
        if (old == null) return old;
        const i = old.findIndex((t) => t.id === savedTodo.id);
        if (i >= 0) return old.map((t) => (t.id === savedTodo.id ? savedTodo : t));
        return [...old, savedTodo];
      });
    },
  });
}

/**
 * Delete a single todo from Supabase. On success removes that todo from ['todos'] cache (mainline-data-flow: 局部更新).
 */
export function useDeleteTodoMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (todoId: string) => {
      const { error } = await deleteTodoFromSupabase(todoId);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_, todoId) => {
      queryClient.setQueryData<Todo[]>(['todos'], (old) => {
        if (old == null) return old;
        return old.filter((t) => t.id !== todoId);
      });
    },
  });
}

/**
 * Delete multiple todos from Supabase. On success removes those ids from ['todos'] cache (mainline-data-flow: 局部更新).
 */
export function useDeleteTodosMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (todoIds: string[]) => {
      const { error } = await deleteTodosFromSupabase(todoIds);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_, todoIds) => {
      const set = new Set(todoIds);
      queryClient.setQueryData<Todo[]>(['todos'], (old) => {
        if (old == null) return old;
        return old.filter((t) => !set.has(t.id));
      });
    },
  });
}
